import { useAuthenticator } from "@aws-amplify/ui-react";
import { trpc } from "../../../utils/trpc";
import { UserContext } from "./UserContext";
import { useEffect, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import type { User } from "@rowan/prisma";
import { ErrorPage } from "../../pages/ErrorPage";
import { LoadingSpinner } from "../../bits/loading";
import mixpanel from "mixpanel-browser";
import { useConfig } from "../../hooks/useConfig";

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [email, setEmail] = useState<string>();
  const { user } = useAuthenticator((context) => [context.user]);
  const { mixpanelEnabled } = useConfig();

  useEffect(() => {
    if (user) {
      const fetchUser = async () => {
        try {
          const session = await fetchAuthSession();
          // The ID Token contains the claims (email, name, etc.)
          const attributes = session.tokens?.idToken?.payload;
          if (!attributes?.email || typeof attributes.email !== "string") {
            throw new Error("Email attribute not found in ID token");
          }
          setEmail(attributes.email);
        } catch (error) {
          console.error("Error fetching user attributes:", error);
        }
      };
      fetchUser();
    }
  }, [user]);

  const { data, isLoading, error } = trpc.getUser.useQuery(undefined, {
    enabled: !!email,
    refetchOnWindowFocus: false,
  });
  const [recentSearches, setRecentSearches] = useState<User["recentSearches"]>(
    [],
  );

  const addNewRecentSearch = (newSearch: User["recentSearches"][number]) => {
    setRecentSearches((prev) => {
      // Check if the search already exists
      // If it does, remove it from the array
      // and add it to the front
      const existingIndex = prev.findIndex(
        (search) =>
          search.query === newSearch.query &&
          search.publication === newSearch.publication,
      );
      if (existingIndex !== -1) {
        const updatedSearches = [...prev];
        updatedSearches.splice(existingIndex, 1);
        return [newSearch, ...updatedSearches];
      }
      // If it doesn't, add it to the front
      // and remove the last one if there are more than 20
      return [newSearch, ...prev.slice(0, 19)];
    });
  };

  useEffect(() => {
    if (data?.recentSearches) {
      setRecentSearches(data.recentSearches);
    }
    if (data?.id && mixpanelEnabled) {
      mixpanel.identify(String(data.id));
    }
  }, [data, mixpanelEnabled]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <UserContext.Provider
      value={{
        user: data ? { ...data, recentSearches } : undefined,
        addNewRecentSearch,
        isLoading,
      }}
    >
      {error && error.message === "Forbidden: User expired" && (
        <ErrorPage
          message="Your user has expired. Please contact support for more information"
          subheader="User expired"
        />
      )}
      {error && error.message !== "Forbidden: User expired" && (
        <ErrorPage message="An unknown error occurred." />
      )}
      {!data && (
        <ErrorPage
          message="User not found. Please contact support for more information"
          subheader="User not found"
        />
      )}
      {!error && data && children}
    </UserContext.Provider>
  );
};
